import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress
} from '@mui/material';
import { Construction as BuildIcon } from '@mui/icons-material';
import { devicesApi } from '../../../../services/apiClient';
import { useThemeSettings } from '../../../../contexts/ThemeContext';

/**
 * Dialog for requesting a physical intervention
 */
const InterventionDialog = ({
  open,
  onClose,
  onSubmit,
  ticket,
  sending = false
}) => {
  const { t } = useThemeSettings();

  // Form state
  const [date, setDate] = useState('');
  const [time, setTime] = useState('09:00');
  const [notes, setNotes] = useState('');
  const [deviceId, setDeviceId] = useState('');

  // Device list state
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);

  // Load devices for the ticket's location
  useEffect(() => {
    const fetchDevices = async () => {
      const locationId = ticket?.locationId || ticket?.location?.id;
      if (!locationId) {
        setDevices([]);
        return;
      }

      try {
        setLoadingDevices(true);
        const response = await devicesApi.getByLocation(locationId);
        const raw = response.data?.devices || response.data?.data || response.data;
        const devicesList = Array.isArray(raw) ? raw : [];
        setDevices(devicesList);
      } catch (error) {
        console.error('Error fetching devices:', error);
        setDevices([]);
      } finally {
        setLoadingDevices(false);
      }
    };

    if (open) {
      fetchDevices();
    }
  }, [open, ticket?.locationId, ticket?.location?.id]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setDate('');
      setTime('09:00');
      setNotes('');
      setDeviceId('');
    }
  }, [open]);

  const handleSubmit = () => {
    onSubmit({
      date,
      time,
      deviceId: deviceId || null,
      notes: notes.trim() || null
    });
  };

  const handleClose = () => {
    if (!sending) {
      setDate('');
      setTime('09:00');
      setNotes('');
      setDeviceId('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {t ? t("Tickets.request_physical_intervention") : "Request Physical Intervention"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t ? t("Tickets.request_physical_intervention_this") : "Request a physical intervention for this ticket. An email notification will be sent to all followers of this ticket."}
        </DialogContentText>

        <TextField
          label={t ? t("Tickets.intervention_date_optional") : "Intervention Date (Optional)"}
          type="date"
          fullWidth
          value={date}
          onChange={(e) => setDate(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
          InputLabelProps={{ shrink: true }}
          helperText={t ? t("Tickets.select_scheduled_date_interv") : "Select the scheduled date for the intervention"}
        />

        <TextField
          label={t ? t("Tickets.intervention_time_optional") : "Intervention Time (Optional)"}
          type="time"
          fullWidth
          value={time}
          onChange={(e) => setTime(e.target.value)}
          sx={{ mb: 2 }}
          InputLabelProps={{ shrink: true }}
          helperText={t ? t("Tickets.select_scheduled_time_interv") : "Select the scheduled time for the intervention (defaults to 9:00 AM)"}
        />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="intervention-device-label">
            {t ? t("Tickets.device_optional") : "Device (Optional)"}
          </InputLabel>
          <Select
            labelId="intervention-device-label"
            id="intervention-device-select"
            value={deviceId}
            label={t ? t("Tickets.device_optional") : "Device (Optional)"}
            onChange={(e) => setDeviceId(e.target.value)}
            disabled={loadingDevices || devices.length === 0}
          >
            <MenuItem value="">
              <em>{t ? t("Tickets.no_specific_device") : "No specific device"}</em>
            </MenuItem>
            {devices.map((device) => (
              <MenuItem key={device.id} value={device.id}>
                {device.friendlyName || device.name || `Device #${device.id}`}
                {device.deviceType && ` (${device.deviceType})`}
              </MenuItem>
            ))}
            {!loadingDevices && devices.length === 0 && (ticket?.locationId || ticket?.location?.id) && (
              <MenuItem disabled>
                <em>{t ? t("Tickets.no_devices_found_this_location") : "No devices found at this location"}</em>
              </MenuItem>
            )}
          </Select>
          {loadingDevices && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {t ? t("Tickets.loading_devices") : "Loading devices..."}
            </Typography>
          )}
          {!loadingDevices && devices.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {t ? t("Tickets.no_devices_found_this_location") : "No devices found at this location"}
            </Typography>
          )}
        </FormControl>

        <TextField
          label={t ? t("Tickets.notes_optional") : "Notes (Optional)"}
          multiline
          rows={4}
          fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t ? t("Tickets.add_any_additional_notes_about_inter") : "Add any additional notes about the intervention..."}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={sending}>
          {t ? t("LocationDialog.cancel") : "Cancel"}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="warning"
          disabled={sending}
          startIcon={sending ? <CircularProgress size={20} /> : <BuildIcon />}
        >
          {sending ? 'Sending...' : 'Send Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InterventionDialog;
